import type { ImageLoaderProps } from "next/image";

/**
 * Custom `next/image` loader: serves storage images through the Bunny CDN pull
 * zone with edge resizing, so every `<NextImage>` emits a real `srcset` ladder.
 *
 * WHY: product/banner images are uploaded at full camera resolution and were
 * served straight from S3 at their native size — the measured LCP hero was
 * 1774x887 painted into a 412x206 slot (4.3x oversized) with `srcset: false`,
 * and the home page shipped ~1.6MB of images. The S3 bucket already sits behind
 * the same pull zone, so the identical path under `cdn.ososs.com` accepts
 * Bunny Optimizer query params — no backend or upload-pipeline change:
 *
 *   S3 original ......................... 96,496 B
 *   ?width=412&quality=75 ............... 11,978 B   (-87%)
 *
 * NOTE: this replaces Next's built-in optimizer rather than adding to it. The
 * resize happens at the CDN edge, so it costs our server nothing — which is why
 * `images.unoptimized` (previously forced on to avoid that server cost) is no
 * longer set. A custom loader is ignored entirely when `unoptimized` is true.
 */

const CDN_ORIGIN = "https://cdn.ososs.com";

/** Storage origins that are mirrored by the CDN pull zone (same path). */
const REWRITABLE_ORIGIN =
  /^https?:\/\/(?:zfloos-(?:production|development)-storage\.s3\.[a-z0-9-]+\.amazonaws\.com|cdn\.ososs\.com)(\/.*)$/i;

/** Vector and animated formats: resizing gains nothing and can break them. */
const SKIP_EXTENSION = /\.(svg|gif)(?:$|\?)/i;

const cdnImageLoader = ({ src, width, quality }: ImageLoaderProps): string => {
  // Local/relative assets (/img/…, /public) are already right-sized and are
  // served by Next itself.
  if (!src || src.startsWith("/") || src.startsWith("data:")) return src;

  if (SKIP_EXTENSION.test(src)) return src;

  const match = src.match(REWRITABLE_ORIGIN);
  // Any other remote host (flagcdn, googleusercontent, unsplash, …) is not in
  // the pull zone — pass it through untouched.
  if (!match) return src;

  const [pathname, existingQuery] = match[1].split("?");
  const params = new URLSearchParams(existingQuery);
  params.set("width", String(width));
  params.set("quality", String(quality ?? 75));

  return `${CDN_ORIGIN}${pathname}?${params.toString()}`;
};

export default cdnImageLoader;
